// Pure chart-data helpers for the network outages overlay on the "Devices network outage" tab.
// Kept free of any React/MUI/apexcharts runtime import so it stays unit-testable through jest
// (type-only imports from 'src/api' are erased at compile time; tooltipHtml is dependency-free).
import type { NetworkOutageItem } from 'src/api'
import { tooltipRow, tooltipTitleBlock } from 'src/components/ui/chart/tooltipHtml'

export const HOUR_MS = 3_600_000

/** Identifies the UTC hour bucket containing a timestamp. */
export const utcHourKey = (datetime: number | string): number => Math.floor(new Date(datetime).getTime() / HOUR_MS)

/**
 * The single dense hourly grid every chart series maps over: `hoursCount` ascending x-values
 * walking back hour-by-hour from `dateTo`. Keeping one builder guarantees the series stay
 * index-aligned — the tooltip and the click handler rely on `dataPointIndex` meaning the
 * same hour in every series.
 */
export const buildHourlyGrid = (dateTo: Date, hoursCount: number): number[] => {
  const grid: number[] = []
  for (let i = 0; i < hoursCount; i++) {
    grid.unshift(new Date(new Date(dateTo).setUTCHours(new Date(dateTo).getUTCHours() - i)).getTime())
  }
  return grid
}

export const DEVICES_COUNT_COLOR = '#1060fe'
export const DEVICES_COUNT_IN_PROGRESS_COLOR = '#9e9e9e'
export const OUTAGE_COLOR = '#d32f2f'

/** True when the UTC hour bucket containing `x` is the current, still-running hour. */
export const isHourInProgress = (x: number, now: number = Date.now()): boolean => utcHourKey(x) === utcHourKey(now)

export interface DevicesCountPoint {
  x: number
  y: number
}

export interface DevicesCountSeries {
  /**
   * One point per grid hour, the in-progress hour included, so the series stays the same length as the
   * outage series with no null gaps: apexcharts positions the shared tooltip from this first series and
   * misplaces it when series lengths differ or when a smooth series ends with a null.
   */
  data: DevicesCountPoint[]
  /** The last grid hour is the current, still-running one; the chart draws that final segment dotted and grey. */
  endsInProgress: boolean
}

/**
 * Builds the devices-count series over the shared hourly grid (`buildHourlyGrid`). The current hour is
 * still collecting readings, so its count always looks like a drop; `endsInProgress` tells the chart to
 * style the final segment differently. When the user pages back in time the grid ends on a complete hour
 * and the flag is false.
 */
export const buildDevicesCountSeriesData = (
  dateTo: Date,
  hoursCount: number,
  deviceCountByHour: Map<number, number>,
  now: number = Date.now()
): DevicesCountSeries => {
  const grid = buildHourlyGrid(dateTo, hoursCount)
  const lastIndex = grid.length - 1

  return {
    data: grid.map((x) => ({ x, y: deviceCountByHour.get(utcHourKey(x)) ?? 0 })),
    endsInProgress: lastIndex >= 0 && isHourInProgress(grid[lastIndex], now)
  }
}

export interface OutagePointMeta {
  outageId: number
  datetime: string
  expectedCount?: number
  actualCount?: number
  brokenCount?: number
  brokenInUseCount?: number
  fileName: string | null
}

export interface OutageChartPoint {
  x: number
  y: number | null
  meta?: OutagePointMeta
}

export interface OutageAnnotationRange {
  x: number
  x2: number
}

/**
 * Builds the outage series over the shared hourly grid (`buildHourlyGrid`), matching outages
 * to grid points by UTC hour bucket. Outage hours get `y = actualCount` and the outage meta;
 * every other hour gets `y = null` so apexcharts breaks the line — consecutive outage
 * hours stay connected while isolated ones render as lone markers.
 */
export const buildOutageSeriesData = (dateTo: Date, hoursCount: number, outages: NetworkOutageItem[]): OutageChartPoint[] => {
  const outagesByHour = new Map<number, NetworkOutageItem>()
  for (const outage of outages) {
    if (outage.datetime) outagesByHour.set(utcHourKey(outage.datetime), outage)
  }
  return buildHourlyGrid(dateTo, hoursCount).map((x) => {
    const outage = outagesByHour.get(utcHourKey(x))
    return outage
      ? {
          x,
          y: outage.actualCount ?? 0,
          meta: {
            outageId: outage.id as number,
            datetime: outage.datetime as string,
            expectedCount: outage.expectedCount,
            actualCount: outage.actualCount,
            brokenCount: outage.brokenCount,
            brokenInUseCount: outage.brokenInUseCount,
            fileName: outage.fileName ?? null
          }
        }
      : { x, y: null }
  })
}

/**
 * Groups runs of adjacent outage points into `[firstX, lastX + 1h]` ranges for
 * xaxis background annotations. Expects the dense ascending grid produced by
 * `buildOutageSeriesData`, so adjacency in the array means consecutive hours.
 */
export const buildOutageAnnotationRanges = (points: OutageChartPoint[]): OutageAnnotationRange[] => {
  const ranges: OutageAnnotationRange[] = []
  let start: number | null = null
  let end: number | null = null

  for (const point of points) {
    if (point.meta) {
      if (start === null) start = point.x
      end = point.x
    } else if (start !== null && end !== null) {
      ranges.push({ x: start, x2: end + HOUR_MS })
      start = null
      end = null
    }
  }
  if (start !== null && end !== null) ranges.push({ x: start, x2: end + HOUR_MS })

  return ranges
}

export interface OutageTooltipInput {
  title: string
  deviceCount: string
  /** The hovered hour is the current one and is still collecting readings. */
  isInProgress?: boolean
  outage?: {
    expectedCount: string
    brokenCount: string
    brokenInUseCount: string
  }
}

/**
 * Builds the custom tooltip HTML: the regular devices-count block (flagged when the hour is still in progress)
 * plus, on outage hours, a red block with the outage counters.
 */
export const buildOutageTooltipHtml = ({ title, deviceCount, isInProgress = false, outage }: OutageTooltipInput): string => {
  const titleBlock = tooltipTitleBlock(title)
  const deviceCountGroup = [
    '<div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">',
    `<span class="apexcharts-tooltip-marker" style="background-color: ${isInProgress ? DEVICES_COUNT_IN_PROGRESS_COLOR : DEVICES_COUNT_COLOR};"></span>`,
    '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">',
    '<div class="apexcharts-tooltip-y-group">',
    `<span class="apexcharts-tooltip-text-y-label">${isInProgress ? 'Devices count (in progress): ' : 'Devices count: '}</span>`,
    `<span class="apexcharts-tooltip-text-y-value">${deviceCount}</span>`,
    '</div>',
    '</div>',
    '</div>'
  ].join('')
  const outageGroup = outage
    ? [
        '<div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2; display: flex;">',
        `<span class="apexcharts-tooltip-marker" style="background-color: ${OUTAGE_COLOR};"></span>`,
        '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">',
        tooltipRow('Supposed to send', outage.expectedCount),
        tooltipRow('Broken', outage.brokenCount),
        tooltipRow('Broken in use', outage.brokenInUseCount),
        '</div>',
        '</div>'
      ].join('')
    : ''

  return titleBlock + deviceCountGroup + outageGroup
}
